import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Check, ExternalLink, Loader2, PenLine, RotateCcw, X } from "lucide-react";
import {
  humanAdoptionPart,
  humanAdoptionSigner,
  humanAdoptionStatus,
} from "@/lib/humanAdoptionStatus";

type Adoption = {
  id: string;
  una_id: string;
  status: string;
  review_status: string;
  review_note: string | null;
  edition_label: string;
  delivered_pdf_path: string | null;
  adoption_effective_date: string | null;
  executed_on: string | null;
  paused_reason: string | null;
  generation_error: string | null;
  footer_verified: boolean | null;
  created_at: string;
};

type Doc = { id: string; part: string; delivered_pdf_path: string | null; page_count: number | null };
type Signer = {
  id: string;
  signer_name: string;
  signer_email: string;
  status: string;
  signed_at: string | null;
};

export default function AdminAdoptionReview() {
  const { id } = useParams<{ id: string }>();
  const [adoption, setAdoption] = useState<Adoption | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [signers, setSigners] = useState<Signer[]>([]);
  const [unaName, setUnaName] = useState<string>("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data: row } = await (supabase as any)
      .from("constitution_adoptions")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (!row) {
      setLoading(false);
      return;
    }

    const [{ data: docRows }, { data: signerRows }, { data: una }] = await Promise.all([
      (supabase as any)
        .from("constitution_adoption_documents")
        .select("id, part, delivered_pdf_path, page_count")
        .eq("adoption_id", id),
      (supabase as any)
        .from("constitution_adoption_signers")
        .select("id, signer_name, signer_email, status, signed_at")
        .eq("adoption_id", id)
        .order("sign_order", { ascending: true }),
      (supabase as any).from("unas").select("name").eq("id", row.una_id).maybeSingle(),
    ]);

    setAdoption(row as Adoption);
    setDocs((docRows ?? []) as Doc[]);
    setSigners((signerRows ?? []) as Signer[]);
    setUnaName(una?.name ?? "");
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const openPath = async (path: string | null) => {
    if (!path) return;
    const { data, error } = await supabase.storage.from("documents").createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) {
      toast.error("Could not open that file");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const decide = async (decision: "approved" | "sent_back") => {
    if (!id) return;
    setBusy(true);
    const { error } = await supabase.functions.invoke("adoption-review-decide", {
      body: { adoption_id: id, decision, note: note.trim() || null },
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(decision === "approved" ? "Approved" : "Sent back");
    setNote("");
    void load();
  };

  const startSigning = async () => {
    if (!id) return;
    setBusy(true);
    const { error } = await supabase.functions.invoke("adoption-signing-start", {
      body: { adoption_id: id },
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Signing session sent");
    void load();
  };

  const cancel = async () => {
    if (!id) return;
    setBusy(true);
    const { error } = await supabase.functions.invoke("adoption-review-decide", {
      body: { adoption_id: id, decision: "cancelled", note: note.trim() || null },
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Adoption stopped");
    void load();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading
      </div>
    );
  }

  if (!adoption) {
    return <div className="p-6 text-sm text-muted-foreground">That adoption was not found.</div>;
  }

  const signedCount = signers.filter((s) => s.status === "signed").length;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/admin/adoptions">
          <ArrowLeft className="mr-2 h-4 w-4" />
          All adoptions
        </Link>
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{unaName || "Association"}</h1>
          <p className="text-sm text-muted-foreground">{adoption.edition_label}</p>
        </div>
        <Badge variant="secondary">{humanAdoptionStatus(adoption.status)}</Badge>
      </div>

      {adoption.generation_error ? (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Generation problem</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">{adoption.generation_error}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Package</CardTitle>
          <CardDescription>
            Footer check: {adoption.footer_verified ? "passed" : "not confirmed"}. Delivered copies
            are flattened image pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {adoption.delivered_pdf_path ? (
            <Button variant="outline" onClick={() => void openPath(adoption.delivered_pdf_path)}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open the full set
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">No delivered file yet.</p>
          )}
          <div className="space-y-1 pt-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 rounded border p-2">
                <span className="text-sm">{humanAdoptionPart(d.part)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!d.delivered_pdf_path}
                  onClick={() => void openPath(d.delivered_pdf_path)}
                >
                  Open
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {adoption.status === "in_review" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review decision</CardTitle>
            <CardDescription>Nothing reaches the member until this passes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note for the record"
              rows={3}
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void decide("approved")} disabled={busy} className="min-h-11">
                <Check className="mr-2 h-4 w-4" />
                Approve and release
              </Button>
              <Button
                variant="outline"
                onClick={() => void decide("sent_back")}
                disabled={busy}
                className="min-h-11"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Send back for rework
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {adoption.status === "ready_to_sign" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signatures</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => void startSigning()} disabled={busy} className="min-h-11">
              <PenLine className="mr-2 h-4 w-4" />
              Send the signing invitations
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Signers: {signedCount} of {signers.length}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {signers.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded border p-2">
              <div>
                <div className="text-sm font-medium">{s.signer_name}</div>
                <div className="text-xs text-muted-foreground">{s.signer_email}</div>
              </div>
              <Badge variant={s.status === "signed" ? "default" : "secondary"}>
                {humanAdoptionSigner(s.status)}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {adoption.status !== "complete" && adoption.status !== "cancelled" ? (
        <Button variant="destructive" onClick={() => void cancel()} disabled={busy}>
          <X className="mr-2 h-4 w-4" />
          Stop this adoption
        </Button>
      ) : null}
    </div>
  );
}
